import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import './TakeTest.css';
import './MockTest.css';

const FLOW_LABELS = {
  quick: '⚡ Quick Practice',
  category: '📂 Category Practice',
  difficulty: '🎯 Difficulty Practice',
  custom: '⚙️ Custom Practice',
};

const MockTestTake = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [instantFeedback, setInstantFeedback] = useState(true);

  const submittedRef = useRef(false);
  const startTime = useRef(Date.now());

  const questions = React.useMemo(() => state?.questions || [], [state]);
  const session_id = state?.session_id;
  const flowMode = state?.flowMode || 'quick';

  // Options are a JSON array of strings; correct_answer is the option VALUE.
  // Resolve the 1-based index of the correct option (0 if not found).
  const optText = (opt) => (opt && typeof opt === 'object' ? (opt.option_text ?? opt.text ?? '') : opt);
  const correctIndexOf = useCallback((qn) => {
    if (!qn || !Array.isArray(qn.options)) return 0;
    const target = String(qn.correct_answer ?? '').trim().toLowerCase();
    const i = qn.options.findIndex(o => String(optText(o)).trim().toLowerCase() === target);
    return i + 1; // 1-based; 0 when not found
  }, []);

  useEffect(() => {
    if (!state) { navigate('/student/mock'); return; }
    setTimeLeft(Math.max(questions.length * 90, 60));
  }, []); // eslint-disable-line

  // ── Timer ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeLeft <= 0 || submitted) return;
    const iv = setInterval(() => {
      setTimeLeft(p => {
        if (p <= 1) {
          clearInterval(iv);
          if (!submittedRef.current) {
            submittedRef.current = true;
            setTimeout(() => handleSubmit(), 0);
          }
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [timeLeft, submitted]); // eslint-disable-line

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (submittedRef.current && submitted) return;
    submittedRef.current = true;
    setSubmitted(true);

    const timeTaken = Math.floor((Date.now() - startTime.current) / 1000);
    const answersArr = questions.map(q => ({
      question_id: q.id,
      selected_option: answers[q.id] !== undefined ? answers[q.id] : null,
      time_spent: 0,
    }));

    try {
      const res = await API.post('/mock/submit', {
        session_id,
        answers: answersArr,
        time_taken_seconds: timeTaken,
      });
      setResult(res.data.result);
    } catch (err) {
      console.error(err);
      setResult({ error: true, correct: 0, wrong: 0, total: questions.length, percentage: '0' });
    }
  }, [answers, questions, session_id, submitted]);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ── No state guard ───────────────────────────────────────────────────────
  if (!state || questions.length === 0) return (
    <div className="tt-loading">
      No questions loaded.
      <button
        onClick={() => navigate('/student/mock')}
        style={{ marginLeft: 12, padding: '6px 16px', background: '#0f3460', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer' }}
      >
        Go Back
      </button>
    </div>
  );

  // ── Result screen ─────────────────────────────────────────────────────────
  if (result) {
    const pct = parseFloat(result.percentage) || 0;
    const pass = pct >= 60;

    // Topic breakdown if available
    const topicBreakdown = result.topic_breakdown || {};

    return (
      <div className="tt-result-screen">
        <div className="tt-result-card" style={{ maxWidth: 560 }}>
          <div className="tt-result-icon">{pass ? '🎉' : '📚'}</div>
          <h2 className="tt-result-title">{pass ? 'Great Practice!' : 'Keep Going!'}</h2>
          <p className="tt-result-sub">{FLOW_LABELS[flowMode]} — Completed</p>

          <div className="tt-result-score">
            <div className="tt-big-score" style={{ color: pass ? '#16a085' : '#e94560' }}>
              {result.correct}/{result.total}
            </div>
            <div className="tt-big-pct" style={{ color: pass ? '#16a085' : '#e94560' }}>
              {pct}%
            </div>
          </div>

          <div style={{
            background: pass ? '#e6f9f0' : '#fff0f0',
            color: pass ? '#1a7f3c' : '#cc0000',
            padding: '8px 24px', borderRadius: 10,
            fontWeight: 700, fontSize: 15, marginBottom: 20,
          }}>
            {pass ? '✓ Good Score' : '✗ Needs Improvement'} — {pct}%
          </div>

          <div className="tt-result-stats">
            <div className="tt-rs">
              <div className="tt-rs-val" style={{ color: '#16a085' }}>{result.correct}</div>
              <div className="tt-rs-label">Correct</div>
            </div>
            <div className="tt-rs">
              <div className="tt-rs-val" style={{ color: '#e94560' }}>{result.wrong}</div>
              <div className="tt-rs-label">Wrong</div>
            </div>
            <div className="tt-rs">
              <div className="tt-rs-val" style={{ color: '#888' }}>
                {result.skipped ?? Math.max(0, result.total - result.correct - (result.wrong || 0))}
              </div>
              <div className="tt-rs-label">Skipped</div>
            </div>
          </div>

          {/* Topic breakdown if available */}
          {Object.keys(topicBreakdown).length > 0 && (
            <div style={{ width: '100%', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10, textAlign: 'left' }}>
                Topic Breakdown
              </div>
              {Object.entries(topicBreakdown).map(([topic, data]) => {
                const tPct = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
                const tColor = tPct >= 80 ? '#16a085' : tPct >= 60 ? '#f39c12' : '#e94560';
                return (
                  <div key={topic} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 120, fontSize: 12, color: '#555', textAlign: 'left', flexShrink: 0 }}>
                      {topic}
                    </div>
                    <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${tPct}%`, height: '100%', background: tColor, borderRadius: 4 }} />
                    </div>
                    <div style={{ width: 44, fontSize: 12, fontWeight: 700, color: tColor, textAlign: 'right', flexShrink: 0 }}>
                      {tPct}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {result.error && (
            <div className="tt-cheat-warning" style={{ background: '#fff0f0', color: '#cc0000' }}>
              ⚠️ There was an issue saving your result.
            </div>
          )}

          <div className="tt-result-actions">
            <button className="tt-btn-home" onClick={() => navigate('/student/mock')}>
              Practice Again
            </button>
            <button className="tt-btn-tests" onClick={() => navigate('/student/mistakes')}>
              Review Mistakes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Test UI ───────────────────────────────────────────────────────────────
  const q = questions[current];
  const answered = Object.keys(answers).length;
  const correctIdx = correctIndexOf(q);

  return (
    <div className="tt-wrapper">

      {/* Top bar */}
      <div className="tt-topbar">
        <div className="tt-topbar-left">
          <div className="tt-test-name">{FLOW_LABELS[flowMode]}</div>
          <div className="tt-session">
            {questions.length} questions · Practice Mode
            <span style={{
              marginLeft: 10, fontSize: 11, fontWeight: 600,
              background: '#e6f9f0', color: '#1a7f3c',
              padding: '2px 8px', borderRadius: 20,
            }}>
              No Proctoring
            </span>
          </div>
        </div>
        <div className="tt-topbar-center">
          <div className={`tt-timer ${timeLeft < 120 ? 'danger' : ''}`}>
            ⏱ {formatTime(timeLeft)}
          </div>
        </div>
        <div className="tt-topbar-right">
          <span className="tt-feedback-label">Instant Feedback</span>
          <div
            className={`tt-toggle ${instantFeedback ? 'on' : ''}`}
            onClick={() => setInstantFeedback(p => !p)}
          >
            <div className="tt-toggle-thumb" />
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="tt-progress-bar">
        <div className="tt-progress-fill"
          style={{ width: `${(answered / questions.length) * 100}%` }} />
      </div>

      <div className="tt-body">

        {/* Question area */}
        <div className="tt-question-area">
          <div className="tt-q-header">
            <span className="tt-q-num">QUESTION {current + 1} OF {questions.length}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {q.topic_name && (
                <span className="tt-q-diff" style={{ background: '#eef2ff', color: '#3c5bd4' }}>
                  {q.topic_name}
                </span>
              )}
              <span className="tt-q-diff" style={{
                background: q.difficulty === 'easy' ? '#e6f9f0'
                  : q.difficulty === 'medium' ? '#fff8e6'
                    : '#fff0f0',
                color: q.difficulty === 'easy' ? '#1a7f3c'
                  : q.difficulty === 'medium' ? '#b37400'
                    : '#cc0000',
              }}>
                {q.difficulty}
              </span>
            </div>
          </div>

          <div className="tt-q-text">{q.question_text}</div>

          <div className="tt-options">
            {(q.options || []).map((opt, i) => {
              const optNum = i + 1;
              const selected = answers[q.id] === optNum;
              const isCorrect = correctIdx === optNum;
              const hasAnswered = answers[q.id] !== undefined;

              let cls = 'tt-option';
              if (selected) cls += ' selected';
              if (instantFeedback && selected && isCorrect) cls += ' correct';
              if (instantFeedback && selected && !isCorrect) cls += ' wrong';
              if (instantFeedback && !selected && isCorrect && hasAnswered) cls += ' show-correct';

              return (
                <div key={i} className={cls}
                  onClick={() => {
                    if (!submitted) {
                      setAnswers(p => ({ ...p, [q.id]: optNum }));
                      setShowExplanation(false);
                    }
                  }}
                >
                  <span className="tt-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="tt-opt-text">{optText(opt)}</span>
                  {/* Show correct/wrong icons when instant feedback is on */}
                  {instantFeedback && selected && (
                    <span style={{ marginLeft: 'auto', fontWeight: 700 }}>
                      {isCorrect ? '✓' : '✗'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {q.explanation && (
            <div className="tt-explanation-toggle"
              onClick={() => setShowExplanation(p => !p)}>
              <span>ℹ View Step-by-Step Explanation</span>
              <span>{showExplanation ? '▲' : '▼'}</span>
            </div>
          )}
          {showExplanation && q.explanation && (
            <div className="tt-explanation">{q.explanation}</div>
          )}

          {/* Auto-show explanation if wrong answer and instant feedback on */}
          {instantFeedback &&
            answers[q.id] !== undefined &&
            answers[q.id] !== correctIdx &&
            q.explanation &&
            !showExplanation && (
              <div style={{
                marginTop: 8, padding: '8px 14px',
                background: '#fff8e6', borderRadius: 8,
                fontSize: 12, color: '#b37400',
              }}>
                💡 Incorrect — click "View Explanation" to understand the answer
              </div>
            )}

          <div className="tt-bottom-nav">
            <button className="tt-btn-prev"
              disabled={current === 0}
              onClick={() => { setCurrent(p => p - 1); setShowExplanation(false); }}>
              ← Previous
            </button>
            <button className="tt-btn-mark"
              onClick={() => setMarked(p => {
                const n = new Set(p);
                n.has(current) ? n.delete(current) : n.add(current);
                return n;
              })}>
              {marked.has(current) ? '★ Marked' : '☆ Mark for Review'}
            </button>
            {current < questions.length - 1 ? (
              <button className="tt-btn-next"
                onClick={() => { setCurrent(p => p + 1); setShowExplanation(false); }}>
                Next →
              </button>
            ) : (
              <button className="tt-btn-submit"
                onClick={() => {
                  if (window.confirm(`Finish practice? Answered ${answered}/${questions.length}`))
                    handleSubmit();
                }}>
                Finish Session
              </button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="tt-sidebar">
          <div className="tt-nav-header">
            Navigator
            <span className="tt-nav-answered">{answered}/{questions.length}</span>
          </div>

          <div className="tt-nav-grid">
            {questions.map((qn, idx) => {
              const sel = answers[qn.id];
              let status = 'unvisited';
              if (marked.has(idx)) status = 'marked';
              else if (sel !== undefined) {
                if (instantFeedback) {
                  status = sel === correctIndexOf(qn) ? 'answered correct' : 'answered wrong';
                } else {
                  status = 'answered';
                }
              }
              return (
                <button key={idx}
                  className={`tt-nav-btn ${status} ${idx === current ? 'current' : ''}`}
                  onClick={() => { setCurrent(idx); setShowExplanation(false); }}>
                  {idx + 1}
                </button>
              );
            })}
          </div>

          <div className="tt-nav-legend">
            <div className="tt-leg"><span className="tt-leg-dot answered" />Answered</div>
            <div className="tt-leg"><span className="tt-leg-dot marked" />Marked</div>
            <div className="tt-leg"><span className="tt-leg-dot unvisited" />Unvisited</div>
            {instantFeedback && (
              <>
                <div className="tt-leg"><span className="tt-leg-dot answered correct" style={{ background: '#16a085' }} />Correct</div>
                <div className="tt-leg"><span className="tt-leg-dot answered wrong" style={{ background: '#e94560' }} />Wrong</div>
              </>
            )}
          </div>

          <button className="tt-finish-btn"
            onClick={() => {
              if (window.confirm(`Finish? Answered ${answered}/${questions.length}`))
                handleSubmit();
            }}>
            Finish Session
          </button>

          <button className="tt-pause-btn" onClick={() => {
            if (window.confirm('Exit practice? Your progress will be lost.'))
              navigate('/student/mock');
          }}>
            Exit Practice
          </button>

          {/* Practice mode info */}
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#e6f9f0', border: '1px solid #b7ebc8',
            borderRadius: 8, fontSize: 11, color: '#1a7f3c', lineHeight: 1.7,
          }}>
            <strong>✅ Practice Mode:</strong><br />
            · No proctoring restrictions<br />
            · Instant feedback available<br />
            · Practice only — does not affect analytics
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTestTake;
